import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircleIcon, ArrowLeftIcon, BriefcaseIcon, CheckIcon, CopyIcon, DownloadIcon, EyeIcon, Loader2Icon, MailIcon, SendIcon, PhoneIcon, Trash2Icon } from 'lucide-react';
import { Button, LinkButton, buttonClasses } from '../../components/ui/Button';
import { SelectField, TextArea, TextField } from '../../components/ui/Field';
import { QuoteLinesEditor } from '../../components/admin/QuoteLinesEditor';
import { QuoteStatusBadge } from '../../components/admin/QuoteStatusBadge';
import { useAdminData } from '../../contexts/AdminDataContext';
import { formatDate, formatDateTime, telHref } from '../../utils/format';
import { QuoteEmailDialog } from '../../components/admin/QuoteEmailDialog';
import { QuotePdfPreview } from '../../components/admin/QuotePdfPreview';
import { downloadQuotePdf } from '../../utils/quotePdf';
import { effectiveStatus, formatKr } from '../../utils/quote';
import { quoteStatuses } from '../../types/quote';
import type { QuoteLine, QuoteStatus } from '../../types/quote';
const ease = [0.23, 1, 0.32, 1] as const;
const SENT_WARNING = 'Dette tilbud er allerede sendt. Ændringer kan betyde, at kunden har en tidligere version.';
export function QuoteDetail() {
  const {
    quoteId
  } = useParams();
  const navigate = useNavigate();
  const {
    quotes,
    cases,
    updateQuote,
    setQuoteStatus,
    duplicateQuote,
    deleteQuote,
    createCaseFromQuote,
    authLoading,
    lastWriteError,
    settings
  } = useAdminData();
  const quote = quotes.find(entry => entry.id === quoteId);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<QuoteLine[]>([]);
  const [quoteDate, setQuoteDate] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  /** Kundeoplysninger og PDF-tekster — alt kan rettes før tilbuddet sendes. */
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [intro, setIntro] = useState('');
  const [terms, setTerms] = useState('');
  const [closing, setClosing] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [working, setWorking] = useState('');
  const [pdfPreview, setPdfPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [composing, setComposing] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    if (!quote) return;
    setTitle(quote.title);
    setDescription(quote.description);
    setLines(quote.lines);
    setQuoteDate(quote.quoteDate);
    setValidUntil(quote.validUntil);
    setInternalNotes(quote.internalNotes);
    setCustomerNotes(quote.customerNotes);
    setCustomerName(quote.customerName);
    setCustomerEmail(quote.customerEmail);
    setCustomerPhone(quote.customerPhone);
    setCustomerAddress(quote.customerAddress);
    setIntro(quote.intro);
    setTerms(quote.terms);
    setClosing(quote.closing);
    setPaymentTerms(quote.paymentTerms);
  }, [quote?.id]);
  if (!quote) return <Navigate to="/admin/tilbud" replace data-id="element-1165" data-mp="39esrz2pl7zk" />;
  const status = effectiveStatus(quote);
  const linkedCase = cases.find(item => item.id === quote.caseId || item.quoteId === quote.id);
  const locked = quote.status === 'Accepteret';
  const history = [...(quote.history ?? [])].sort((a, b) => b.at.localeCompare(a.at));
  async function handleSave() {
    if (saving) return;
    // Der skrives aldrig, mens Firebase stadig gendanner sessionen.
    if (authLoading) {
      setError('Sessionen indlæses stadig. Prøv igen om et øjeblik.');
      return;
    }
    if (locked) {
      const confirmed = window.confirm('Tilbuddet er accepteret af kunden. Er du sikker på, at du vil ændre pris og indhold?');
      if (!confirmed) return;
    }
    // Et allerede sendt tilbud må aldrig ændres i stilhed.
    if (quote.status === 'Sendt' && !window.confirm(`${SENT_WARNING}\n\nVil du gemme alligevel?`)) {
      return;
    }
    setSaving(true);
    setError('');
    const result = await updateQuote(quote.id, {
      title,
      description,
      lines,
      quoteDate,
      validUntil,
      internalNotes,
      customerNotes,
      customerName,
      customerEmail,
      customerPhone,
      customerAddress,
      intro,
      terms,
      closing,
      paymentTerms
    });
    setSaving(false);
    if (!result.ok) {
      setError(result.error ?? 'Ændringerne kunne ikke gemmes.');
      return;
    }
    setSaved(true);
  }
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-1166" data-mp="2xr8mki812t6m">
      <Link to="/admin/tilbud" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-1167" data-mp="2xoe06l5geyak">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1168" data-mp="37gs4nlnx2j0f" />
        Tilbud
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-1169" data-mp="2jr7mh4zl7ps7">
        <div data-id="element-1170" data-mp="2eso0j4kzudzu">
          <p className="text-sm text-ink-muted" data-id="element-1171" data-mp="2eswaanltl36t">{quote.quoteNumber}</p>
          <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-1172" data-mp="2tp5abrdjbkka">
            {quote.title}
          </h1>
          <p className="mt-1.5 text-[15px] text-ink-soft" data-id="element-1173" data-mp="2os50r6hh7zof">
            {quote.customerName} · {formatKr(quote.total)} inkl. moms
          </p>
        </div>
        <div className="flex flex-col items-start gap-3 sm:items-end" data-id="element-1174" data-mp="3yrwa5eg8bma">
          <QuoteStatusBadge status={status} data-id="element-1175" data-mp="b39kezim69oe" />
          <SelectField id="tilbud-status" label="Status" className="w-52" value={quote.status} onChange={async event => {
          setWorking('status');
          setError('');
          const result = await setQuoteStatus(quote.id, event.target.value as QuoteStatus);
          setWorking('');
          if (!result.ok) {
            setError(result.error ?? 'Statussen kunne ikke gemmes. Prøv igen.');
          }
        }} options={quoteStatuses.filter(option => option !== 'Udløbet').map(option => ({
          value: option,
          label: option
        }))} data-id="element-1176" data-mp="b2hy6fawpoas" />
        </div>
      </div>

      {sentTo && <p role="status" className="mt-5 flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink" data-id="element-1177" data-mp="35ren96n1somd">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-1178" data-mp="3aoeql9sijd9s">
            <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1179" data-mp="1ntjopv2qdxmc" />
          </span>
          Tilbuddet er sendt til {sentTo}
        </p>}

      {quote.status === 'Sendt' && <p className="mt-5 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-1180" data-mp="1iwjjfmdpgywa">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1181" data-mp="1dxq73xa5ndxp" />
          {SENT_WARNING}
        </p>}

      {status === 'Udløbet' && <p className="mt-5 flex items-start gap-2 rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink-soft" data-id="element-1182" data-mp="143zjeulur7j2">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" data-id="element-1183" data-mp="101uta5um4t16" />
          Tilbuddet er udløbet ({formatDate(quote.validUntil)}). Forlæng gyldigheden, eller lav en kopi.
        </p>}

      {quote.status === 'Accepteret' && <section className="mt-5 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-1184" data-mp="l7zobaxkvnv9">
          <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-1185" data-mp="2a1x8zcizni35">
            <div data-id="element-1186" data-mp="2e2z9rxsk0278">
              <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1187" data-mp="2e1fsq4z2pn4e">
                {linkedCase ? 'Sag allerede oprettet' : 'Tilbuddet er accepteret'}
              </h2>
              <p className="mt-1 text-[15px] text-ink-soft" data-id="element-1188" data-mp="250zrz39psu38">
                {linkedCase ? `Sag #${linkedCase.number} — ${linkedCase.title}` : 'Opret sagen, så arbejdet kan planlægges og udføres.'}
              </p>
            </div>
            {linkedCase ? <Link to={`/admin/sager/${linkedCase.id}`} className={buttonClasses('secondary', 'md')} data-id="element-1189" data-mp="1hczqb8av7j9v">
                <BriefcaseIcon className="h-4 w-4" aria-hidden="true" data-id="element-1190" data-mp="1mai3xbztumo7" />
                Åbn sag
              </Link> : <Button size="md" disabled={working === 'case'} onClick={async () => {
          setWorking('case');
          setError('');
          const created = await createCaseFromQuote(quote);
          setWorking('');
          if (!created) {
            setError('Sagen kunne ikke oprettes. Prøv igen.');
            return;
          }
          navigate(`/admin/sager/${created.id}`);
        }} data-id="element-1191" data-mp="17eso5vl8j5rx">
                {working === 'case' ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1192" data-mp="1bo38uidpyruk" />
                    Opretter …
                  </> : 'Opret sag fra tilbud'}
              </Button>}
          </div>
        </section>}

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.5fr_0.5fr]" data-id="element-1193" data-mp="1gho8sfyvhvwe">
        <div className="space-y-5" data-id="element-1194" data-mp="1vf89haoql4wr">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-1195" data-mp="1vc2lxzvrab35">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1196" data-mp="1qgna1dhb6p9d">
              Kunde på tilbuddet
            </h2>
            <p className="mt-1.5 text-sm text-ink-muted" data-id="element-1197" data-mp="655x7etp6xcq">
              Oplysningerne her vises på PDF’en og kan rettes uden at ændre kundekartoteket.
            </p>
            <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1198" data-mp="3sjffr7ay5b5l">
              <TextField id="td-kundenavn" label="Navn" value={customerName} onChange={event => {
              setCustomerName(event.target.value);
              setSaved(false);
            }} data-id="element-1199" data-mp="3ohu75y5g3j17" />
              <TextField id="td-kundeadresse" label="Adresse" value={customerAddress} onChange={event => {
              setCustomerAddress(event.target.value);
              setSaved(false);
            }} data-id="element-1200" data-mp="g18xef4zolx1" />
              <TextField id="td-kundemail" label="E-mail" type="email" value={customerEmail} onChange={event => {
              setCustomerEmail(event.target.value);
              setSaved(false);
            }} data-id="element-1201" data-mp="2vswovv18hm8g" />
              <TextField id="td-kundetlf" label="Telefon" type="tel" value={customerPhone} onChange={event => {
              setCustomerPhone(event.target.value);
              setSaved(false);
            }} data-id="element-1202" data-mp="1kb0td8g6dndf" />
            </div>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1203" data-mp="1z200ef1d43uw">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1204" data-mp="2401j2yuvm15u">Opgave</h2>
            {locked && <p className="mt-3 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-1205" data-mp="27yitbr9lfrrh">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1206" data-mp="dmkglfc2sdqu" />
                Tilbuddet er accepteret. Ændringer i pris og indhold skal bekræftes, før de gemmes.
              </p>}
            <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1207" data-mp="1bzgdh6ver4hg">
              <TextField id="td-titel" label="Tilbudstitel" className="sm:col-span-2" value={title} onChange={event => {
              setTitle(event.target.value);
              setSaved(false);
            }} data-id="element-1208" data-mp="1c0zq8nhuuxeh" />
              <TextField id="td-dato" label="Tilbudsdato" type="date" value={quoteDate} onChange={event => {
              setQuoteDate(event.target.value);
              setSaved(false);
            }} data-id="element-1209" data-mp="1uxspr1hm8uh9" />
              <TextField id="td-gyldig" label="Gyldig til" type="date" value={validUntil} onChange={event => {
              setValidUntil(event.target.value);
              setSaved(false);
            }} data-id="element-1210" data-mp="2imbux85dcr54" />
            </div>
            <TextArea id="td-beskrivelse" label="Beskrivelse af arbejdet" className="mt-5" rows={4} value={description} onChange={event => {
            setDescription(event.target.value);
            setSaved(false);
          }} data-id="element-1211" data-mp="328dexo30rzax" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1212" data-mp="2jajalncu6byv">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1213" data-mp="24l4zoatm3mhd">Tilbudslinjer</h2>
            <div className="mt-5" data-id="element-1214" data-mp="29k7tt08208ak">
              <QuoteLinesEditor lines={lines} onChange={next => {
              setLines(next);
              setSaved(false);
            }} data-id="element-1215" data-mp="29l9hq5a64p9p" />
            </div>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1216" data-mp="20lmd7am573cf">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1217" data-mp="1sic23732dfxk">Noter</h2>
            <TextArea id="td-kundenoter" label="Bemærkninger til kunden" className="mt-5" rows={3} value={customerNotes} onChange={event => {
            setCustomerNotes(event.target.value);
            setSaved(false);
          }} data-id="element-1218" data-mp="1nl22iylynnw0" />
            <TextArea id="td-internenoter" label="Interne noter" hint="Vises aldrig på kundens PDF." className="mt-5" rows={3} value={internalNotes} onChange={event => {
            setInternalNotes(event.target.value);
            setSaved(false);
          }} data-id="element-1219" data-mp="14t9b38mjiaqe" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1220" data-mp="2q6m4hn6ri5p7">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1221" data-mp="2q4hq2bb8loao">Tekst på tilbud</h2>
            <p className="mt-1.5 text-sm text-ink-muted" data-id="element-1222" data-mp="bx0fhmj054wt">
              Teksterne herunder står på PDF’en og kan skrives individuelt for hvert tilbud.
            </p>
            <TextArea id="td-indledning" label="Indledning" className="mt-5" rows={3} value={intro} onChange={event => {
            setIntro(event.target.value);
            setSaved(false);
          }} data-id="element-1223" data-mp="lpxtbfd1u5p3" />
            <TextArea id="td-betingelser" label="Betingelser" className="mt-5" rows={3} value={terms} onChange={event => {
            setTerms(event.target.value);
            setSaved(false);
          }} data-id="element-1224" data-mp="3u4ptpbbic4hk" />
            <TextField id="td-betaling" label="Betalingsbetingelser" className="mt-5" value={paymentTerms} onChange={event => {
            setPaymentTerms(event.target.value);
            setSaved(false);
          }} data-id="element-1225" data-mp="1di6jy2m6k3tr" />
            <TextArea id="td-afslutning" label="Afsluttende tekst" className="mt-5" rows={2} value={closing} onChange={event => {
            setClosing(event.target.value);
            setSaved(false);
          }} data-id="element-1226" data-mp="3og7tqoatcnul" />
          </section>

          {error && <p role="alert" className="flex items-start gap-2 text-[15px] text-danger" data-id="element-1227" data-mp="2quaaupsiugi4">
              <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1228" data-mp="35iy8r2pub2b8" />
              {error}
            </p>}

          <div className="flex flex-wrap items-center gap-3" data-id="element-1229" data-mp="2d12l73o03v32">
            <Button size="lg" onClick={handleSave} disabled={saving} data-id="element-1230" data-mp="2cz809pcqc2yz">
              {saving ? <>
                  <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1231" data-mp="28054ywmjxb47" />
                  Gemmer …
                </> : 'Gem ændringer'}
            </Button>
            <Button variant="secondary" size="lg" onClick={() => setPdfPreview(true)} data-id="element-1232" data-mp="ejinsgxv1l01">
              <EyeIcon className="h-4 w-4" aria-hidden="true" data-id="element-1233" data-mp="ehfqdbudopla" />
              Forhåndsvis PDF
            </Button>
            <Button variant="secondary" size="lg" disabled={downloading} onClick={async () => {
            setDownloading(true);
            setError('');
            try {
              await downloadQuotePdf(quote, settings);
            } catch {
              setError('PDF’en kunne ikke dannes. Prøv igen.');
            }
            setDownloading(false);
          }} data-id="element-1234" data-mp="16aohofmzmmgv">
              {downloading ? <>
                  <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1235" data-mp="3n03s3kfjrpwt" />
                  Danner PDF …
                </> : <>
                  <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1236" data-mp="dhmtkzt778xk" />
                  Download PDF
                </>}
            </Button>
            <Button variant="dark" size="lg" onClick={() => setComposing(true)} data-id="element-1237" data-mp="8l5w3f03zi7t">
              <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-1238" data-mp="3ljk18w0o80l" />
              Send tilbud på mail
            </Button>
            {saved && <p role="status" className="flex items-center gap-2 text-[15px] text-ink" data-id="element-1239" data-mp="w8w51emj47rs">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-1240" data-mp="rhxnyctpv5nh">
                  <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1241" data-mp="mjme423f8v59" />
                </span>
                Ændringerne er gemt
              </p>}
          </div>

          <div className="flex flex-wrap items-center gap-3" data-id="element-1242" data-mp="1f7i8h05iqayv">
            <Button variant="secondary" size="md" disabled={working === 'accept' || quote.status === 'Accepteret'} onClick={async () => {
            setWorking('accept');
            setError('');
            const result = await setQuoteStatus(quote.id, 'Accepteret');
            setWorking('');
            if (!result.ok) setError(result.error ?? 'Statussen kunne ikke gemmes.');
          }} data-id="element-1243" data-mp="1f673ix0c6x66">
              Marker som accepteret
            </Button>
            <Button variant="secondary" size="md" disabled={working === 'reject' || quote.status === 'Afvist'} onClick={async () => {
            setWorking('reject');
            setError('');
            const result = await setQuoteStatus(quote.id, 'Afvist');
            setWorking('');
            if (!result.ok) setError(result.error ?? 'Statussen kunne ikke gemmes.');
          }} data-id="element-1244" data-mp="2agc1zflwzcys">
              Marker som afvist
            </Button>
            <button type="button" onClick={() => setConfirmingDelete(true)} className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-line px-4 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-danger/40 hover:text-danger" data-id="element-1245" data-mp="3so8jzb2jjlz3">
              <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-1246" data-mp="afhlcr74qnjy" />
              Slet tilbud
            </button>
          </div>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1247" data-mp="kfi348pcovm1">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1248" data-mp="kdf8tch60gbi">Historik</h2>
            <ol className="mt-4 space-y-3" data-id="element-1249" data-mp="p8uids853q24">
              {history.map(event => <li key={event.id} className="flex gap-3 text-[15px]" data-id="element-1250" data-mp="u7nv3iflrd18">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" data-id="element-1251" data-mp="y8hhu8hb6lsj" />
                  <span data-id="element-1252" data-mp="2notl8pbyuh7a">
                    <span className="text-ink" data-id="element-1253" data-mp="2np3ceg8qyuek">{event.text}</span>
                    <span className="block text-sm text-ink-muted" data-id="element-1254" data-mp="2ipgy03h6n846">
                      {formatDateTime(event.at)}
                      {event.by ? ` · ${event.by}` : ''}
                    </span>
                  </span>
                </li>)}
              {history.length === 0 && <li className="text-sm text-ink-muted" data-id="element-1255" data-mp="36fbbmn9bt0ph">Ingen aktivitet registreret endnu.</li>}
            </ol>
          </section>
        </div>

        <aside className="space-y-5" data-id="element-1256" data-mp="32faxz4u1wtjh">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-1257" data-mp="2xly9cupdgmqq">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1258" data-mp="2snem8qz1l4ws">Kunde</h2>
            <p className="mt-3 font-display text-lg font-bold tracking-tight text-ink" data-id="element-1259" data-mp="3q2sh6bz92jau">
              {quote.customerName || '—'}
            </p>
            <ul className="mt-3 space-y-1.5 text-[15px] text-ink-soft" data-id="element-1260" data-mp="3lb0y0mifbfuq">
              <li data-id="element-1261" data-mp="82esd4jcij7a">{quote.customerPhone || '—'}</li>
              <li className="break-all" data-id="element-1262" data-mp="83orzy5ujzg1">{quote.customerEmail || '—'}</li>
              <li data-id="element-1263" data-mp="d0yi7g128f4x">{quote.customerAddress || '—'}</li>
            </ul>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row" data-id="element-1264" data-mp="3vg9388ywpygd">
              {quote.customerPhone && <LinkButton href={telHref(quote.customerPhone)} variant="secondary" size="sm" data-id="element-1265" data-mp="3boppz2b0uga">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1266" data-mp="3lha0f70tox1a" />
                  Ring
                </LinkButton>}
              {quote.customerEmail && <LinkButton href={`mailto:${quote.customerEmail}`} variant="secondary" size="sm" data-id="element-1267" data-mp="3ch4uz2f6w8pr">
                  <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-1268" data-mp="3giqev7q55n9g" />
                  Send e-mail
                </LinkButton>}
            </div>
            {quote.customerId && <Link to={`/admin/kunder/${quote.customerId}`} className="mt-4 inline-block text-sm font-medium text-accent hover:text-accent-deep" data-id="element-1269" data-mp="1erd9khk9hdcv">
                Se kundeprofil
              </Link>}
          </section>

          {linkedCase && <section className="rounded-card border border-line bg-white p-6" data-id="element-1270" data-mp="38yxl7r4iio37">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1271" data-mp="3911zn301f5hq">
                Tilknyttet sag
              </h2>
              <p className="mt-3 text-[15px] text-ink" data-id="element-1272" data-mp="3iwls1ffe7m1q">
                Sag #{linkedCase.number} — {linkedCase.title}
              </p>
              <Link to={`/admin/sager/${linkedCase.id}`} className={`${buttonClasses('secondary', 'sm')} mt-4`} data-id="element-1273" data-mp="3dy0rtg0552da">
                Åbn sag
              </Link>
            </section>}

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1274" data-mp="1xjsfsfczybfw">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1275" data-mp="1xlvh3vjlnqzx">Beløb</h2>
            <dl className="mt-4 divide-y divide-line/70 text-[15px]" data-id="element-1276" data-mp="1snbsfndmg13v">
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1277" data-mp="1uz2sqnmzxqjq">
                <dt className="text-ink-soft" data-id="element-1278" data-mp="1z0g237pasbve">Ekskl. moms</dt>
                <dd className="font-medium text-ink" data-id="element-1279" data-mp="1z32a18g3ote5">{formatKr(quote.subtotal)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1280" data-mp="23yhg3e7v9l00">
                <dt className="text-ink-soft" data-id="element-1281" data-mp="28yvj2hum24r6">Moms</dt>
                <dd className="font-medium text-ink" data-id="element-1282" data-mp="1bc56aiygl2dz">{formatKr(quote.vatAmount)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1283" data-mp="1ga6lutwoejkp">
                <dt className="font-medium text-ink" data-id="element-1284" data-mp="1g8de1babttb4">Total</dt>
                <dd className="font-display text-lg font-bold text-ink" data-id="element-1285" data-mp="1l8879lhkquzc">{formatKr(quote.total)}</dd>
              </div>
            </dl>
            {quote.lastSentAt ? <p className="mt-4 text-sm text-ink-muted" data-id="element-1286" data-mp="slx90hhfx65p">
                Sendt til {quote.sentTo} den {formatDateTime(quote.lastSentAt)}
              </p> : <p className="mt-4 text-sm text-ink-muted" data-id="element-1287" data-mp="q0pe0vvf0ils">Tilbuddet er endnu ikke sendt til kunden.</p>}
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1288" data-mp="l2ny2jtaczej">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1289" data-mp="g208m2kpgqmf">Handlinger</h2>
            <div className="mt-4 flex flex-col gap-2" data-id="element-1290" data-mp="b3yhqvh1fn0e">
              <Button variant="secondary" size="sm" disabled={working === 'copy'} onClick={async () => {
              setWorking('copy');
              setError('');
              const copy = await duplicateQuote(quote);
              setWorking('');
              if (!copy) {
                setError(lastWriteError() || 'Tilbuddet kunne ikke duplikeres. Prøv igen.');
                return;
              }
              navigate(`/admin/tilbud/${copy.id}`);
            }} data-id="element-1291" data-mp="b5j8ea08elsz">
                <CopyIcon className="h-4 w-4" aria-hidden="true" data-id="element-1292" data-mp="3fjri9l8mgf8a" />
                Dupliker tilbud
              </Button>
            </div>
          </section>
        </aside>
      </div>

      {pdfPreview && <QuotePdfPreview quote={quote} onClose={() => setPdfPreview(false)} data-id="element-1293" data-mp="39tjlby8o4rm1" />}

      {composing && <QuoteEmailDialog quote={quote} onClose={() => setComposing(false)} onSent={email => {
      setComposing(false);
      setSentTo(email);
    }} data-id="element-1294" data-mp="2vxy1e5haky0h" />}

      {confirmingDelete && <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-5" role="dialog" aria-modal="true" aria-labelledby="slet-tilbud-titel" data-id="element-1295" data-mp="3smds44w51df6">
          <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.2,
        ease
      }} className="w-full max-w-md rounded-card bg-white p-6" data-id="element-1296" data-mp="3jsse4nrss06t">
            <h2 id="slet-tilbud-titel" className="font-display text-xl font-bold tracking-tight text-ink" data-id="element-1297" data-mp="g6jxy7mj7us4">
              Er du sikker på, at du vil slette dette tilbud?
            </h2>
            <p className="mt-3 text-[15px] text-ink-soft" data-id="element-1298" data-mp="lfuri12p5v7b">
              {quote.quoteNumber} slettes permanent.
            </p>
            {linkedCase && <p className="mt-4 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-1299" data-mp="ubaga3olhk61">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1300" data-mp="6l6octkrr8uv" />
                Tilbuddet er knyttet til sag #{linkedCase.number}. Sagen bliver ikke slettet, men mister
                henvisningen til tilbuddet.
              </p>}
            {error && <p role="alert" className="mt-4 text-sm text-danger" data-id="element-1301" data-mp="bizqnbiybipv">
                {error}
              </p>}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse" data-id="element-1302" data-mp="1s043q9d8pefx">
              <Button variant="dark" disabled={deleting} onClick={async () => {
            if (deleting) return;
            setDeleting(true);
            setError('');
            const result = await deleteQuote(quote.id);
            setDeleting(false);
            if (!result.ok) {
              setError(result.error ?? 'Tilbuddet kunne ikke slettes. Prøv igen.');
              return;
            }
            navigate('/admin/tilbud');
          }} data-id="element-1303" data-mp="1my50r0lewjji">
                {deleting ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1304" data-mp="91633ckg5uk9" />
                    Sletter …
                  </> : 'Slet tilbud'}
              </Button>
              <Button variant="secondary" disabled={deleting} onClick={() => setConfirmingDelete(false)} data-id="element-1305" data-mp="2et35j6awh82z">
                Annuller
              </Button>
            </div>
          </motion.div>
        </div>}
    </div>;
}